import {
  backendFetchResponse,
  type BackendRequestInit,
} from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";
import { getActualViewer } from "@/data/viewer";
import type { GameAddressResolution, GamePageContext } from "../../game-types";
import { parseGamePagePath, type GamePagePath } from "./path";
import { resolvedGamePage } from "./resolution";
import { type SelectedGameUpload } from "./canonical-path";

const backendBase = "/api/observatory";

function gameReadFailure({
  status,
  unlisted,
}: {
  readonly status: number;
  readonly unlisted: boolean;
}): GameAddressResolution {
  switch (status) {
    case 401:
      return {
        kind: "navigate",
        navigation: { kind: "sign-in" },
        viewer: null,
      };
    case 403:
      return { kind: "not-found" };
    case 404:
      return { kind: unlisted ? "not-found" : "unmatched" };
    default:
      return { kind: "unavailable" };
  }
}

export async function resolveGameAddress({
  href,
  signal,
}: {
  readonly href: string;
  readonly signal?: AbortSignal | undefined;
}): Promise<GameAddressResolution> {
  const path = parseGamePagePath({ href });

  if (path.kind !== "path") return path;

  const viewer = await getActualViewer({ signal });
  const init = {
    credentials: viewer ? "same-origin" : "omit",
    headers: { "X-Use-Elevated-Privileges": "false" },
    signal: signal ?? null,
    timeoutMs: 10_000,
  } as const;
  const version =
    path.tail.length === 0 ? path.url.searchParams.get("version") : null;
  const selected = version
    ? await backendFetchResponse(
        backendBase,
        `/v2/coworlds/${encodeURIComponent(version)}`,
        init,
      )
    : null;

  if (selected?.ok === false) {
    const status = selected.status === 404 && !viewer ? 401 : selected.status;
    return gameReadFailure({ status, unlisted: true });
  }

  const selectedUpload: SelectedGameUpload = selected
    ? await selected.json()
    : null;
  return readGameRouting({ path, selectedUpload, viewer, init });
}

async function readGameRouting({
  path,
  selectedUpload,
  viewer,
  init,
}: {
  readonly path: GamePagePath;
  readonly selectedUpload: SelectedGameUpload;
  readonly viewer: GamePageContext["viewer"];
  readonly init: BackendRequestInit;
}): Promise<GameAddressResolution> {
  const { route, unlisted, decodedSlug } = path;
  const lookupSlug =
    selectedUpload?.game_address?.slug ?? decodedSlug.toLowerCase();
  const requestedLeague = route.area === "game" ? (route.league ?? null) : null;
  const query = new URLSearchParams();

  if (requestedLeague) query.set("league", requestedLeague);

  const endpoint = `/v2/game-addresses/${encodeURIComponent(lookupSlug)}/routing`;
  const routing = await backendFetchResponse(
    backendBase,
    `${endpoint}${query.size ? `?${query}` : ""}`,
    init,
  );
  let response = routing;
  const defaultFallback =
    response.status === 404 && route.name === "game.league";

  if (defaultFallback)
    response = await backendFetchResponse(backendBase, endpoint, init);

  if (!response.ok)
    return gameReadFailure({ status: response.status, unlisted });

  const resolution: Schemas["GameAddressRouting"] = await response.json();
  return resolvedGamePage({
    path,
    resolution,
    selectedUpload,
    requestedLeague,
    defaultFallback,
    viewer,
    frontDoor: async () => {
      const frontDoorRead = await backendFetchResponse(
        backendBase,
        endpoint,
        init,
      );

      if (!frontDoorRead.ok) return null;

      const bare: Schemas["GameAddressRouting"] = await frontDoorRead.json();
      return bare.league?.id ?? null;
    },
  });
}
